/**
 * RBAC-4.3 · FR-32. Remove everything somebody can do, now, for the day they leave.
 *
 * The confirm says when it takes effect (AC 1) because the database makes it true: the person is
 * deactivated, banned, and their sessions and tokens end in the same transaction (SUB-1.37). Copy is
 * `lib/personAccess.ts` (tested); this file lays it out. Refusals (yourself, the last administrator,
 * a privileged holder) are the database's sentences, shown as they come.
 */
import { useState } from 'react';
import { Loader2, ShieldOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { removeAccessCopy, removeAccessErrorMessage } from '@/lib/personAccess';
import { PersonAccessError, removePersonAccess } from '@/services/personAccessService';

export interface RemoveAccessDialogProps {
  open: boolean;
  userId: string;
  firstName: string;
  onOpenChange: (open: boolean) => void;
  onRemoved: () => void;
}

export function RemoveAccessDialog({ open, userId, firstName, onOpenChange, onRemoved }: RemoveAccessDialogProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const copy = removeAccessCopy({ firstName });

  const confirm = async () => {
    setSaving(true);
    setError(null);
    try {
      await removePersonAccess(userId);
      onRemoved();
      onOpenChange(false);
    } catch (e) {
      setError(removeAccessErrorMessage(e instanceof PersonAccessError ? e.rpc : { message: e instanceof Error ? e.message : undefined }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (saving) return;
        if (!o) setError(null);
        onOpenChange(o);
      }}
    >
      <DialogContent className="sm:max-w-[520px]" data-testid="remove-access-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldOff className="h-5 w-5 text-destructive" aria-hidden />
            {copy.title}
          </DialogTitle>
          <DialogDescription data-testid="remove-access-effect">{copy.effect}</DialogDescription>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">{copy.kept}</p>

        {error && (
          <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            {error}
          </p>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={() => void confirm()} disabled={saving}>
            {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            {copy.confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
